import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../api'

export const createBooking = createAsyncThunk(
  'booking/createBooking',
  async (bookingData, { rejectWithValue }) => {
    console.log("DEBUG: Creating booking with data:", bookingData);
    try {
      const response = await api.post('/booking/create', bookingData)
      console.log("DEBUG: Booking creation response:", response.data)
      return response.data
    } catch (error) {
      console.error(
        'Error during booking creation:',
        error.response ? error.response.data : error.message
      )
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

// Get a booking by ID
export const getBookingById = createAsyncThunk(
  'booking/getBookingById',
  async (bookingId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/booking/bookings/${bookingId}`) // Fixed path
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

// Update booking status
export const updateBookingStatus = createAsyncThunk(
  'booking/updateBookingStatus',
  async ({ bookingId, bookingStatus }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/bookings/${bookingId}/status`, {
        status: bookingStatus // Changed property name to 'status'
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

// Transfer a plot
export const transferPlot = createAsyncThunk(
  'booking/transferPlot',
  async (transferData, { rejectWithValue }) => {
    try {
      const response = await api.post('/plots/transfer', transferData) // Fixed path
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

export const updateBooking = createAsyncThunk(
  'booking/updateBooking',
  async ({ bookingId, bookingData }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/booking/bookings/${bookingId}`,
        bookingData
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

// Soft delete a booking
export const deleteBooking = createAsyncThunk(
  'booking/deleteBooking',
  async (bookingId, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/booking/bookings/${bookingId}`)
      // console.log(response) // Fixed path
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

export const getAllBookings = createAsyncThunk(
  'booking/getAllBookings',
  async (
    { page, limit, societyId, status, sort, payment_mode, customerId } = {},
    { rejectWithValue }
  ) => {
    try {
      // If customerId is provided, use customer-specific endpoint
      if (customerId) {
        const response = await api.get(`/booking/bookings/customer/${customerId}`)
        // Transform response to match expected format
        return {
          bookings: response.data.data || [],
          page: 1,
          limit: response.data.data?.length || 0,
          total: response.data.data?.length || 0,
          totalPages: 1
        }
      } else {
        // Use admin endpoint for all bookings
        const params = { page, limit, societyId, status, sort }
        if (payment_mode) params.payment_mode = payment_mode
        const response = await api.get('/booking/bookings', { params })
        return response.data // { bookings, page, limit, total, totalPages }
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

// Hold Booking Management
export const getHoldBookings = createAsyncThunk(
  'booking/getHoldBookings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/booking/hold-bookings')
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

export const getExpiredHoldBookings = createAsyncThunk(
  'booking/getExpiredHoldBookings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/booking/hold-bookings/expired')
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

export const completeHoldBooking = createAsyncThunk(
  'booking/completeHoldBooking',
  async (
    {
      bookingId,
      remaining_amount,
      receipt_no,
      payment_mode,
      installment_years,
      installment_months,
      initial_payment
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.put(
        `/booking/hold-bookings/${bookingId}/complete`,
        {
          remaining_amount,
          receipt_no,
          payment_mode,
          installment_years,
          installment_months,
          initial_payment
        }
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

export const cancelExpiredHoldBookings = createAsyncThunk(
  'booking/cancelExpiredHoldBookings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/booking/hold-bookings/cancel-expired')
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

export const editHoldBooking = createAsyncThunk(
  'booking/editHoldBooking',
  async (
    {
      bookingId,
      plot_id,
      customer_id,
      total_amount,
      hold_amount,
      hold_days,
      receipt_no,
      dealer_name,
      dealer_phone,
      dealer_comission,
      dealer_commission_date,
      offertag,
      booking_date
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.put(`/booking/hold-bookings/${bookingId}/edit`, {
        plot_id,
        customer_id,
        total_amount,
        hold_amount,
        hold_days,
        receipt_no,
        dealer_name,
        dealer_phone,
        dealer_comission,
        dealer_commission_date,
        offertag,
        booking_date
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

const bookingSlice = createSlice({
  name: 'booking',
  initialState: {
    bookings: [],
    booking: null,
    loading: false,
    error: null,
    status: 'idle',
    pagination: { page: 1, limit: 20, total: 0, totalPages: 1 },
    filters: { societyId: undefined, status: undefined, sort: '-created_at' },
    holdBookings: [],
    expiredHoldBookings: []
  },
  reducers: {
    setBookingFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    }
  },
  extraReducers: builder => {
    builder
      .addCase(createBooking.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(createBooking.fulfilled, (state, action) => {
        state.loading = false
        // Do not add the new booking optimistically; rely on backend fetch/socket for correct data
        if (!action.payload?.booking) {
          console.error('Invalid booking data:', action.payload)
        }
      })
      .addCase(createBooking.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(getBookingById.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getBookingById.fulfilled, (state, action) => {
        state.loading = false
        state.booking = action.payload // Assuming the payload contains the booking data
      })
      .addCase(getBookingById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(updateBookingStatus.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateBookingStatus.fulfilled, (state, action) => {
        state.loading = false
        state.booking = action.payload // Assuming the payload contains the booking data
      })
      .addCase(updateBookingStatus.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(transferPlot.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(transferPlot.fulfilled, (state, action) => {
        state.loading = false
        state.booking = action.payload // Adjust based on actual response
      })
      .addCase(transferPlot.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(deleteBooking.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteBooking.fulfilled, (state, action) => {
        state.loading = false
        state.booking = null
        const deletedBookingId = action.payload.booking?._id || action.meta.arg
        if (deletedBookingId) {
          // Remove from bookings array
          state.bookings = state.bookings.filter(
            b => b._id !== deletedBookingId
          )
          // Remove from holdBookings array
          if (state.holdBookings) {
            state.holdBookings = state.holdBookings.filter(
              b => b._id !== deletedBookingId
            )
          }
        }
      })
      .addCase(deleteBooking.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(updateBooking.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateBooking.fulfilled, (state, action) => {
        state.loading = false
        state.booking = action.payload.booking
        const index = state.bookings.findIndex(
          b => b._id === action.payload.booking._id
        )
        if (index !== -1) {
          state.bookings[index] = action.payload.booking
        }
      })
      .addCase(updateBooking.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(getAllBookings.pending, state => {
        if (state.status === 'idle') {
          state.loading = true
          state.status = 'loading'
          state.error = null
        }
      })
      .addCase(getAllBookings.fulfilled, (state, action) => {
        state.loading = false
        state.status = 'succeeded'
        state.bookings = action.payload.bookings || []
        state.pagination = {
          page: action.payload.page || 1,
          limit: action.payload.limit || 20,
          total: action.payload.total || 0,
          totalPages: action.payload.totalPages || 1
        }
      })

      .addCase(getAllBookings.rejected, (state, action) => {
        state.loading = false
        state.status = 'failed'
        state.error = action.payload
      })

      .addCase(getHoldBookings.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getHoldBookings.fulfilled, (state, action) => {
        state.loading = false
        state.holdBookings = action.payload.holdBookings || []
      })
      .addCase(getHoldBookings.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(getExpiredHoldBookings.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getExpiredHoldBookings.fulfilled, (state, action) => {
        state.loading = false
        state.expiredHoldBookings = action.payload.holdBookings || []
      })
      .addCase(getExpiredHoldBookings.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(completeHoldBooking.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(completeHoldBooking.fulfilled, (state, action) => {
        state.loading = false
        if (state.holdBookings) {
          state.holdBookings = state.holdBookings.filter(
            booking => booking._id !== action.payload.booking._id
          )
        }
        const index = state.bookings.findIndex(
          booking => booking._id === action.payload.booking._id
        )
        if (index !== -1) {
          state.bookings[index] = action.payload.booking
        }
      })
      .addCase(completeHoldBooking.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(cancelExpiredHoldBookings.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(cancelExpiredHoldBookings.fulfilled, (state, action) => {
        state.loading = false
        if (state.holdBookings && action.payload.cancelledBookings) {
          const cancelledIds = action.payload.cancelledBookings.map(
            booking => booking._id
          )
          state.holdBookings = state.holdBookings.filter(
            booking => !cancelledIds.includes(booking._id)
          )
        }
      })
      .addCase(cancelExpiredHoldBookings.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(editHoldBooking.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(editHoldBooking.fulfilled, (state, action) => {
        state.loading = false
        if (state.holdBookings) {
          const index = state.holdBookings.findIndex(
            booking => booking._id === action.payload.booking._id
          )
          if (index !== -1) {
            state.holdBookings[index] = action.payload.booking
          }
        }
      })
      .addCase(editHoldBooking.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const { setBookingFilters } = bookingSlice.actions
export default bookingSlice.reducer
